import React, { Dispatch, SetStateAction } from 'react';
import styled from 'styled-components';
import { HoverImage } from '../../lib/styled';
import { I_GRADIENT_SCHEDULE, I_POPUP_EXIT } from '../../types/images';
import { Col, Row } from '../../lib/utils';
import { Body1Bold, Body3Regular, CaptionLight } from '../../lib/font';
import { colors } from '../../lib/colors';
import { useCenterFeatureFlags } from '../../hooks/useCenterFeatureFlags';
import { Ban, CalendarClock, CalendarDays, Repeat2, Target, UsersRound } from 'lucide-react';

const ModalOverLay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: #00000047;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;

const ModalInner = styled.div`
    background: white;
    width: 640px;
    max-height: calc(100vh - 48px);
    overflow-y: auto;
    right: 0;
    border-radius: 12px;
    box-shadow: 8px 8px 28px 0px #00000040;
    padding-bottom: 36px;
    background: #f5f9ff;
    display: flex;
    flex-direction: column;
`;

const ModalHeader = styled.div`
    position: relative;
    height: 104px;
    display: flex;
    align-items: flex-end;
    justify-content: center;
`;

const HeaderTitle = styled(Col)`
    width: 220px;
    height: 80px;
    align-items: center;
`;

const CloseButton = styled(HoverImage)`
    position: absolute;
    top: 18px;
    right: 20px;
    width: 24px;
    height: 24px;
`;

const NewOpportunityGuide = styled.div`
    width: 568px;
    margin-top: 28px;
    padding: 22px 24px 24px;
    border-radius: 12px;
    border: 1px solid #dce6f8;
    background: #f8fbff;
    box-sizing: border-box;
`;

const ScheduleTypeButton = styled.button<{ $variant?: 'default' | 'group' | 'opportunity' }>`
    width: 272px;
    height: 80px;
    border-radius: 12px;
    border: 1px solid
        ${({ $variant }) =>
            $variant === 'opportunity' ? '#BFD0FA' : $variant === 'group' ? '#C9D8F8' : '#DCE6F8'};
    background: ${({ $variant }) =>
        $variant === 'opportunity' ? '#F7FAFF' : $variant === 'group' ? '#F8FBFF' : '#FFFFFF'};
    color: #17264a;
    display: flex;
    align-items: center;
    padding: 0 20px;
    box-sizing: border-box;
    text-align: left;
    box-shadow: 0 4px 12px rgba(23, 38, 74, 0.05);
    transition:
        border-color 0.16s ease,
        box-shadow 0.16s ease,
        transform 0.16s ease,
        background 0.16s ease;

    &:hover {
        border-color: ${({ $variant }) => ($variant === 'opportunity' ? '#3D73DD' : '#8EACED')};
        background: ${({ $variant }) => ($variant === 'opportunity' ? '#F0F5FF' : '#F8FBFF')};
        box-shadow: 0 8px 20px rgba(61, 115, 221, 0.12);
        transform: translateY(-1px);
    }

    &:active {
        transform: translateY(0);
        box-shadow: 0 4px 10px rgba(61, 115, 221, 0.1);
    }
`;

const FullScheduleTypeButton = styled(ScheduleTypeButton)`
    width: 568px;
`;

const NewOpportunityButton = styled(ScheduleTypeButton)`
    width: 520px;
    height: 68px;
`;

const ButtonIconWrap = styled.span<{ $variant?: 'default' | 'group' | 'opportunity' }>`
    width: 42px;
    height: 42px;
    border-radius: 12px;
    margin-right: 14px;
    flex: 0 0 42px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: ${({ $variant }) =>
        $variant === 'opportunity' ? '#E9F1FF' : $variant === 'group' ? '#EEF4FF' : '#F3F6FB'};
    color: ${({ $variant }) => ($variant === 'opportunity' || $variant === 'group' ? colors.BLUE_500 : '#667085')};
`;

const ButtonCopy = styled.span`
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 5px;
`;

const ButtonTitle = styled.span`
    color: #17264a;
    font-size: 18px;
    font-weight: 800;
    line-height: 22px;
`;

const ButtonDescription = styled.span`
    color: #7d8aa2;
    font-size: 13px;
    font-weight: 600;
    line-height: 17px;
`;

type Props = {
    modalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    navigate: any;
    createAlert: any;
    selectedSchedule: any;
    setSelectedSchedule: any;
    calendarType: 'daily' | 'weekly';
    calendarSelectedDay: string;
    calendarScrollY: number;
    onClickGroupSession?: () => void;
};

type ScheduleActionButtonProps = {
    title: string;
    description: string;
    icon: React.ReactNode;
    onClick: () => void;
    full?: boolean;
    opportunity?: boolean;
    group?: boolean;
};

const ScheduleActionButton = ({
    title,
    description,
    icon,
    onClick,
    full = false,
    opportunity = false,
    group = false,
}: ScheduleActionButtonProps) => {
    const variant = opportunity ? 'opportunity' : group ? 'group' : 'default';
    const Button = full ? FullScheduleTypeButton : ScheduleTypeButton;

    return (
        <Button type="button" $variant={variant} onClick={onClick}>
            <ButtonIconWrap $variant={variant}>{icon}</ButtonIconWrap>
            <ButtonCopy>
                <ButtonTitle>{title}</ButtonTitle>
                <ButtonDescription>{description}</ButtonDescription>
            </ButtonCopy>
        </Button>
    );
};

const ModalCreateClass = ({
    modalVisible,
    setModalVisible,
    navigate,
    createAlert,
    selectedSchedule,
    setSelectedSchedule,
    calendarType,
    calendarSelectedDay,
    calendarScrollY,
    onClickGroupSession,
}: Props) => {
    const { isNewOpportunityEnabled } = useCenterFeatureFlags();
    const closeModal = () => {
        setModalVisible(false);
        setSelectedSchedule(null);
    };

    const navigateCreateSchedule = (type: string) => {
        navigate('/schedule/create', {
            state: {
                type,
                selectedSchedule: selectedSchedule,
                calendarType,
                calendarSelectedDay,
                calendarScrollY,
            },
        });
    };

    if (!modalVisible) return null;

    return (
        <ModalOverLay
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                    closeModal();
                }
            }}
        >
            <ModalInner role="dialog" aria-modal="true" aria-label="새로운 일정 생성">
                <ModalHeader>
                    <HeaderTitle>
                        <img src={I_GRADIENT_SCHEDULE} alt="" style={{ width: 52, height: 52 }} />
                        <Body1Bold style={{ color: colors.LAVEL_4, fontSize: 18, marginTop: 4 }}>
                            {'새로운 일정 생성'}
                        </Body1Bold>
                    </HeaderTitle>
                    <CloseButton
                        src={I_POPUP_EXIT}
                        onClick={closeModal}
                    />
                </ModalHeader>

                <Col style={{ width: 568, alignSelf: 'center', marginTop: 28, paddingBottom: 36 }}>
                    <Row style={{ width: 568, justifyContent: 'space-between' }}>
                        <ScheduleActionButton
                            title="수업 일정"
                            description="1회 수업 등록"
                            icon={<CalendarDays size={22} strokeWidth={2.2} />}
                            onClick={() => {
                                navigateCreateSchedule('oneSchedule');
                                console.log('🚀 ~ calendarScrollY:', calendarScrollY);
                                console.log('🚀 ~ calendarSelectedDay:', calendarSelectedDay);
                                console.log('🚀 ~ calendarType:', calendarType);
                                console.log('🚀 ~ selectedSchedule:', selectedSchedule);
                            }}
                        />
                        <ScheduleActionButton
                            title="반복수업 일정"
                            description="정기 수업 반복 생성"
                            icon={<Repeat2 size={22} strokeWidth={2.2} />}
                            onClick={() => navigateCreateSchedule('repeatSchedule')}
                        />
                    </Row>

                    <Row style={{ width: 568, justifyContent: 'space-between', marginTop: 24 }}>
                        <ScheduleActionButton
                            title="일반 일정"
                            description="상담, 미팅, 개인 일정"
                            icon={<CalendarClock size={22} strokeWidth={2.2} />}
                            onClick={() => navigateCreateSchedule('etcSchedule')}
                        />
                        <ScheduleActionButton
                            title="OFF 일정"
                            description="근무 제외 시간"
                            icon={<Ban size={22} strokeWidth={2.2} />}
                            onClick={() => navigateCreateSchedule('offSchedule')}
                        />
                    </Row>

                    <div style={{ marginTop: 24 }}>
                        <ScheduleActionButton
                            full
                            group
                            title="그룹 / 프라이빗 수업"
                            description="여러 회원이 참여하는 수업 일정"
                            icon={<UsersRound size={22} strokeWidth={2.2} />}
                            onClick={() => {
                                setModalVisible(false);
                                onClickGroupSession && onClickGroupSession();
                            }}
                        />
                    </div>

                    {isNewOpportunityEnabled && (
                        <NewOpportunityGuide>
                            <Row style={{ alignItems: 'center', marginBottom: 12 }}>
                                <ButtonIconWrap $variant="opportunity">
                                    <Target size={22} strokeWidth={2.2} />
                                </ButtonIconWrap>
                                <Col style={{ minWidth: 0 }}>
                                    <Body3Regular style={{ color: colors.LAVEL_4, fontSize: 17, fontWeight: 800 }}>
                                        신규기회
                                    </Body3Regular>
                                    <CaptionLight style={{ color: colors.LAVEL_2, marginTop: 7, fontSize: 13, lineHeight: '20px' }}>
                                        수업 전 단계의 전환 후보 일정입니다. 캘린더와 메모에만 기록되고 수강권 차감, 정산에는 연결되지 않습니다.
                                    </CaptionLight>
                                </Col>
                            </Row>
                            <NewOpportunityButton
                                type="button"
                                $variant="opportunity"
                                onClick={() => navigateCreateSchedule('newOpportunitySchedule')}
                            >
                                <ButtonIconWrap $variant="opportunity">
                                    <Target size={21} strokeWidth={2.2} />
                                </ButtonIconWrap>
                                <ButtonCopy>
                                    <ButtonTitle>신규기회 만들기</ButtonTitle>
                                    <ButtonDescription>OT, 상담, 체험 같은 전환 후보 기록</ButtonDescription>
                                </ButtonCopy>
                            </NewOpportunityButton>
                        </NewOpportunityGuide>
                    )}
                </Col>
            </ModalInner>
        </ModalOverLay>
    );
};

export default ModalCreateClass;
